import { useCallback, useEffect, useRef, useState } from 'react'
import { type LoginResponse } from '../api/auth'
import {
  costCentresApi,
  loadDimensionOptions,
  EMPTY_DIMENSION_OPTIONS,
  type CostCentre,
  type DimensionOption,
  type DimensionOptions,
  PAYMENT_METHODS,
} from '../api/costs'
import {
  expensesApi,
  expenseCategoriesApi,
  suppliersApi,
  financialAccountsApi,
  type Expense,
  type ExpenseCreateBody,
  type ExpenseUpdateBody,
  type ExpenseCategory,
  type SupplierOption,
  type FinancialAccountOption,
} from '../api/finance-ext'
import { useFinanceDimensions } from '../lib/financeDimensions'
import { financeControlsApi, type ApprovalInfo } from '../api/financeControls'
import { ApiError } from '../api/client'
import { SortKeyTh, SortResetLink } from '../components/SortKeyTh'
import { Button, Callout, Card, CardContent, DataTable, Dialog, EmptyState, FilterBar, FormGrid, FormSection, IconButton, LoadingState, PageHeader, Page, Pagination, Select, StatusPill, Tabs, TextField, type PillTone } from '../components/ui'

/**
 * TASK-0276 (F-022, REQ-145-001) — UI delle spese aziendali, chiusura del gruppo aperto da F-021
 * (`ExpensesController`/`ExpenseCategoriesController`, già integrati e con le tabelle applicate in
 * produzione).
 *
 * <b>Niente tabella di transizioni qui.</b> I pulsanti "Cambia stato" mostrano ESATTAMENTE
 * `expense.availableTransitions` (vedi `finance-ext.ts`), il campo che `Expense.AvailableTransitions`
 * calcola lato server da `ExpenseStatusTransitions` — la stessa tabella applicata che
 * `ExpensesController.Transition` usa per accettare o rifiutare la richiesta. Non esiste in questo
 * file nessuna copia degli otto stati o degli archi ammessi: se il server cambia il grafo, questa UI
 * si adatta senza modifiche.
 *
 * <b>Autorizzazione — chi vede non è chi decide.</b> `GET/POST/PUT /api/expenses` sono
 * `EqualStaff` (chiunque registra/modifica una spesa pianificata), `POST .../transition` e
 * `DELETE` sono `Admin` (vedi `ExpensesController`'s own remarks per il perché — ogni transizione,
 * non solo un sottoinsieme). Questa pagina nasconde i pulsanti "Cambia stato"/"Elimina" a chi non è
 * Admin invece di mostrarli disabilitati: un 403 non è mai raggiungibile dall'interfaccia per un
 * ruolo che non ne ha diritto, e il motivo (riservato agli amministratori) è scritto accanto allo
 * stato al posto dei pulsanti.
 *
 * <b>L'invariante netto+IVA=totale resta del server.</b> Il form calcola il totale mentre l'utente
 * digita SOLO per aiutarlo (anteprima, nessuna scrittura), ma non lo invia mai: `netAmount`/
 * `vatAmount` sono gli unici due campi di importo nel payload, esattamente come
 * `ExpenseCreateRequest`/`ExpenseUpdateRequest` li dichiarano — `TotalAmount` è calcolato e
 * verificato dal dominio/CHECK del database, mai da questo file.
 *
 * <b>Fornitori e conti possono essere zero righe.</b> In produzione `crm.suppliers` e
 * `crm.financial_accounts` sono vuote (verificato prima di scrivere questa pagina) — i due selettori
 * mostrano sempre l'opzione "Nessuno" più le righe disponibili, mai un errore quando la lista è
 * vuota.
 */

const STATUS_LABELS: Record<string, string> = {
  Prevista: 'Prevista',
  Approvata: 'Approvata',
  DaPagare: 'Da pagare',
  ParzialmentePagata: 'Parzialmente pagata',
  Pagata: 'Pagata',
  Annullata: 'Annullata',
  Contestata: 'Contestata',
  Scaduta: 'Scaduta',
}

function pillTone(color: string | undefined): PillTone {
  switch (color) {
    case 'info': return 'info'
    case 'warning': return 'warning'
    case 'success': return 'success'
    case 'danger': return 'error'
    default: return 'neutral'
  }
}

const STATUS_COLORS: Record<string, string> = {
  Prevista: 'secondary',
  Approvata: 'info',
  DaPagare: 'warning',
  ParzialmentePagata: 'warning',
  Pagata: 'success',
  Annullata: 'dark',
  Contestata: 'danger',
  Scaduta: 'danger',
}

const ALL_STATUSES = Object.keys(STATUS_LABELS)

const PAGE_SIZE = 25

/** Stesso schema di `€ 1.234,56` usato da `CommissionsPage.tsx`'s `formatEur` — `toLocaleString`
 * con 2 decimali fissi, mai un `number` grezzo, così un importo non perde mai i centesimi in
 * visualizzazione. */
function formatEur(n: number): string {
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(s?: string): string {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('it-IT')
}

/** Stesso meccanismo di rilevazione 403 di `OutboxPage.tsx`/`CommissionsPage.tsx` (TASK-0098):
 * `api()` lancia un `Error` semplice il cui messaggio è il `title`/`message`/`error` JSON del
 * backend, o — per una risposta senza corpo come il 403 di `[Authorize]` — lo `statusText` di
 * fetch ("Forbidden"). */
function isForbidden(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : ''
  return msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')
}

/** Verbatim lo stesso controllo Admin di `ContestDetailPage.tsx`/`CompanyDetailPage.tsx`/
 * `FormDetailPage.tsx` — `PolicyNames.Admin` grants esattamente questi due ruoli server-side. */
function isAdmin(auth: LoginResponse): boolean {
  // F-069: l'Amministrazione ha gli stessi poteri dell'Admin sulle spese (PolicyNames.FinanceAdministration).
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin' || auth.role === 'EqualAmministrazione'
}

/** Verbatim lo stesso controllo EqualStaff di `CommissionsPage.tsx`/`ContractFormPage.tsx` —
 * `PolicyNames.EqualStaff` grants esattamente questi ruoli server-side. */
function isEqualStaff(auth: LoginResponse): boolean {
  return [
    'Administrator',
    'EqualAdmin',
    'EqualDirettoreGenerale',
    'EqualDirettoreCommerciale',
    'EqualAreaManager',
    'EqualOperatore',
    'EqualBackOffice',
    // F-069: scrive come lo staff (PolicyNames.FinanceStaffWrite); il Commercialista resta in sola lettura.
    'EqualAmministrazione',
  ].includes(auth.role)
}

interface ExpenseFormState {
  supplierId: string
  expenseDate: string
  description: string
  expenseCategoryId: string
  costCentre: string
  costCentreId: string
  netAmount: string
  vatAmount: string
  dueDate: string
  expectedAccountId: string
  notes: string
  // F-092: dimensioni analitiche (id come testo, vuoto = non assegnata).
  operatorId: string
  productId: string
  campaignId: string
  contestId: string
  agentUserId: string
  projectCode: string
  // R3-10 (flag finanza.dimensioni_estese): metodo di pagamento, BU, sede e team.
  paymentMethod: string
  businessUnitId: string
  siteId: string
  teamId: string
}

const EMPTY_FORM: ExpenseFormState = {
  supplierId: '',
  expenseDate: new Date().toISOString().slice(0, 10),
  description: '',
  expenseCategoryId: '',
  costCentre: '',
  costCentreId: '',
  netAmount: '',
  vatAmount: '',
  dueDate: '',
  expectedAccountId: '',
  notes: '',
  operatorId: '',
  productId: '',
  campaignId: '',
  contestId: '',
  agentUserId: '',
  projectCode: '',
  paymentMethod: '',
  businessUnitId: '',
  siteId: '',
  teamId: '',
}

/**
 * F-092 / REQ-149-001 — selettore di una dimensione analitica. Con la lista disponibile è un menu a tendina; se la
 * lista è vuota (es. gli utenti li legge solo l'Admin) ripiega sull'inserimento dell'id, così la dimensione resta
 * assegnabile. L'id non viene verificato dal server: appartiene a un altro modulo.
 */
function DimensionPicker({ label, options, value, onChange }: {
  label: string
  options: DimensionOption[]
  value: string
  onChange: (v: string) => void
}) {
  const known = value === '' || options.some(o => String(o.id) === value)
  return (
    <>
      {options.length > 0 ? (
        <Select label={label} value={value} onChange={e => onChange(e.target.value)}>
          <option value="">Non assegnato</option>
          {!known && <option value={value}>#{value}</option>}
          {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </Select>
      ) : (
        <TextField
          label={label}
          type="number"
          min={1}
          placeholder="ID (facoltativo)"
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      )}
    </>
  )
}

/** Id intero positivo dal testo del form, altrimenti `undefined`. */
function dimId(v: string): number | undefined {
  const n = Number(v)
  return Number.isInteger(n) && n > 0 ? n : undefined
}

/** Per la modifica: valore scelto; se la dimensione c'era e ora è vuota, `0` = toglierla; altrimenti non toccare. */
function dimIdForUpdate(v: string, original?: number | null): number | undefined {
  return dimId(v) ?? (original ? 0 : undefined)
}

interface Props {
  auth: LoginResponse
}

export function ExpensesPage({ auth }: Props) {
  const [tab, setTab] = useState<'expenses' | 'categories'>('expenses')
  const canWrite = isEqualStaff(auth)
  const canTransition = isAdmin(auth)
  // Il pulsante "Nuova spesa" sta nell'header; il form vive nel tab: il contatore ne richiede l'apertura.
  const [createRequest, setCreateRequest] = useState(0)

  return (
    <Page>
      <PageHeader
        title="Spese aziendali"
        icon="receipt_long"
        actions={tab === 'expenses' && canWrite ? (
          <Button type="button" variant="filled" icon="add" onClick={() => setCreateRequest(n => n + 1)}>
            Nuova spesa
          </Button>
        ) : undefined}
      />

      <Tabs
        aria-label="Sezioni spese"
        value={tab}
        onChange={id => setTab(id as 'expenses' | 'categories')}
        items={[
          { id: 'expenses', label: 'Spese', icon: 'receipt_long' },
          { id: 'categories', label: 'Categorie', icon: 'category' },
        ]}
      />

      {tab === 'expenses'
        ? <ExpensesTab canWrite={canWrite} canTransition={canTransition} createRequest={createRequest} />
        : <CategoriesTab canWrite={canWrite} />}
    </Page>
  )
}

/* ── Spese ───────────────────────────────────────────────────────────────────────────────────── */

type ScadenzaFilter = 'all' | 'overdue' | 'none'

/** F-101: ordinamento di default di GET /api/expenses (data spesa decrescente). */
const DEFAULT_SORT = 'date_desc'

function ExpensesTab({ canWrite, canTransition, createRequest }: { canWrite: boolean; canTransition: boolean; createRequest: number }) {
  const [items, setItems] = useState<Expense[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [unauthorized, setUnauthorized] = useState(false)

  const [categories, setCategories] = useState<ExpenseCategory[]>([])
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([])
  // F-064: centri di costo dell'anagrafica (menu a tendina accanto al testo libero storico).
  const [costCentres, setCostCentres] = useState<CostCentre[]>([])
  const [accounts, setAccounts] = useState<FinancialAccountOption[]>([])
  // F-092: liste per le dimensioni analitiche e spesa in modifica (serve a sapere quali dimensioni togliere).
  const [dimensionOptions, setDimensionOptions] = useState<DimensionOptions>(EMPTY_DIMENSION_OPTIONS)
  const [editOriginal, setEditOriginal] = useState<Expense | null>(null)
  // R3-10: dimensioni estese (spente = nessun campo in piu').
  const finDims = useFinanceDimensions()

  // Filtri e ordinamento: tutti lato server (F-101). La scadenza (`due`) conta anche le pagine successive;
  // "scadute" esclude le spese già pagate o annullate.
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [scadenzaFilter, setScadenzaFilter] = useState<ScadenzaFilter>('all')
  const [sort, setSort] = useState(DEFAULT_SORT)

  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<ExpenseFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const [transitioning, setTransitioning] = useState<number | null>(null)
  // F-079: stato delle approvazioni delle spese Previste (per riga), rifiuto in corso e avviso duplicati.
  const [approvalInfo, setApprovalInfo] = useState<Record<number, ApprovalInfo>>({})
  const [deciding, setDeciding] = useState<number | null>(null)
  const [rejectingId, setRejectingId] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [duplicateNotice, setDuplicateNotice] = useState<number[] | null>(null)
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  // F-104: motivo del cestino e collegamenti che bloccano lo spostamento (409 HAS_DEPENDENCIES: solo conteggi).
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteDeps, setDeleteDeps] = useState<Record<string, number> | null>(null)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    setUnauthorized(false)
    try {
      const res = await expensesApi.list({
        status: statusFilter || undefined,
        expenseCategoryId: categoryFilter ? Number(categoryFilter) : undefined,
        due: scadenzaFilter,
        sort,
        page,
        size: PAGE_SIZE,
      })
      setItems(res.items)
      setTotal(res.total)
      // F-079: n. approvazioni raccolte / richieste per le sole spese ancora Previste. Un errore qui non
      // blocca la lista: senza info la riga resta quella di sempre.
      const pending = res.items.filter(x => x.status === 'Prevista')
      const entries = await Promise.all(
        pending.map(x => financeControlsApi.approvals(x.id).then(i => [x.id, i] as const).catch(() => null)),
      )
      setApprovalInfo(Object.fromEntries(entries.filter((e): e is readonly [number, ApprovalInfo] => e !== null)))
    } catch (e) {
      if (isForbidden(e)) {
        setUnauthorized(true)
      } else {
        setError(e instanceof Error ? e.message : 'Errore durante il caricamento')
      }
    } finally {
      setLoading(false)
    }
  }, [statusFilter, categoryFilter, scadenzaFilter, sort, page])

  useEffect(() => { void load() }, [load])

  function handleSort(next: string) { setSort(next); setPage(1) }

  // Anagrafiche di supporto — caricate una volta, non ripaginate: 22 categorie (seed), e
  // fornitori/conti che oggi in produzione sono 0 righe (i due selettori devono comunque
  // funzionare, vedi questo file's own class-level remarks).
  useEffect(() => {
    expenseCategoriesApi.list().then(setCategories).catch(() => setCategories([]))
    suppliersApi.list().then(setSuppliers).catch(() => setSuppliers([]))
    costCentresApi.list().then(setCostCentres).catch(() => setCostCentres([]))
    financialAccountsApi.list().then(setAccounts).catch(() => setAccounts([]))
    loadDimensionOptions().then(setDimensionOptions).catch(() => setDimensionOptions(EMPTY_DIMENSION_OPTIONS))
  }, [])

  // Nome di una dimensione per la riga della tabella; senza lista si mostra l'id.
  const dimensionLabel = useCallback(
    (kind: keyof DimensionOptions, id?: number | null) =>
      id ? dimensionOptions[kind].find(o => o.id === id)?.label ?? `#${id}` : null,
    [dimensionOptions],
  )

  const categoryName = useCallback(
    (id: number) => categories.find(c => c.id === id)?.name ?? `#${id}`,
    [categories],
  )
  const supplierName = useCallback(
    (id?: number) => (id ? suppliers.find(s => s.id === id)?.companyName ?? `#${id}` : '—'),
    [suppliers],
  )

  // F-101: la scadenza è filtrata dal server, non più sulla sola pagina aperta.
  const visibleItems = items

  function openCreate() {
    setForm(EMPTY_FORM)
    setEditOriginal(null)
    setEditId(null)
    setSaveError('')
    setShowForm(true)
  }

  // Richiesta di creazione dal pulsante dell'header (il contatore parte da 0: nessuna apertura al montaggio).
  const lastCreateRequest = useRef(createRequest)
  useEffect(() => {
    if (createRequest === lastCreateRequest.current) return
    lastCreateRequest.current = createRequest
    openCreate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createRequest])

  function openEdit(x: Expense) {
    setForm({
      supplierId: x.supplierId ? String(x.supplierId) : '',
      expenseDate: x.expenseDate.slice(0, 10),
      description: x.description,
      expenseCategoryId: String(x.expenseCategoryId),
      costCentre: x.costCentre ?? '',
      costCentreId: x.costCentreId ? String(x.costCentreId) : '',
      netAmount: String(x.netAmount),
      vatAmount: String(x.vatAmount),
      dueDate: x.dueDate ? x.dueDate.slice(0, 10) : '',
      expectedAccountId: x.expectedAccountId ? String(x.expectedAccountId) : '',
      notes: x.notes ?? '',
      operatorId: x.operatorId ? String(x.operatorId) : '',
      productId: x.productId ? String(x.productId) : '',
      campaignId: x.campaignId ? String(x.campaignId) : '',
      contestId: x.contestId ? String(x.contestId) : '',
      agentUserId: x.agentUserId ? String(x.agentUserId) : '',
      projectCode: x.projectCode ?? '',
      paymentMethod: x.paymentMethod ?? '',
      businessUnitId: x.businessUnitId ? String(x.businessUnitId) : '',
      siteId: x.siteId ? String(x.siteId) : '',
      teamId: x.teamId ? String(x.teamId) : '',
    })
    setEditOriginal(x)
    setEditId(x.id)
    setSaveError('')
    setShowForm(true)
  }

  function closeForm() {
    setShowForm(false)
    setEditId(null)
    setSaveError('')
  }

  const netPreview = Number(form.netAmount.replace(',', '.'))
  const vatPreview = Number(form.vatAmount.replace(',', '.'))
  // Sola ANTEPRIMA per l'utente mentre digita — non è mai la verità e non è mai inviata al server:
  // il totale reale è calcolato e vincolato dal dominio (Expense.SetAmounts) e dal CHECK
  // CK_expenses_net_plus_vat_equals_total sul database. Se uno dei due campi non è ancora un
  // numero valido, non si mostra un totale fuorviante.
  const totalPreview = Number.isFinite(netPreview) && Number.isFinite(vatPreview) && form.netAmount !== '' && form.vatAmount !== ''
    ? netPreview + vatPreview
    : null

  async function handleSave() {
    if (!form.description.trim()) { setSaveError('La descrizione è obbligatoria'); return }
    if (!form.expenseCategoryId) { setSaveError('La categoria è obbligatoria'); return }
    const net = Number(form.netAmount.replace(',', '.'))
    const vat = Number(form.vatAmount.replace(',', '.'))
    if (!Number.isFinite(net) || net < 0) { setSaveError('Importo netto non valido'); return }
    if (!Number.isFinite(vat) || vat < 0) { setSaveError('IVA non valida'); return }

    setSaving(true)
    setSaveError('')
    try {
      if (editId !== null) {
        const body: ExpenseUpdateBody = {
          supplierId: form.supplierId ? Number(form.supplierId) : undefined,
          expenseDate: form.expenseDate || undefined,
          description: form.description,
          expenseCategoryId: Number(form.expenseCategoryId),
          costCentre: form.costCentre || undefined,
          costCentreId: form.costCentreId ? Number(form.costCentreId) : undefined,
          netAmount: net,
          vatAmount: vat,
          dueDate: form.dueDate || undefined,
          expectedAccountId: form.expectedAccountId ? Number(form.expectedAccountId) : undefined,
          notes: form.notes || undefined,
          operatorId: dimIdForUpdate(form.operatorId, editOriginal?.operatorId),
          productId: dimIdForUpdate(form.productId, editOriginal?.productId),
          campaignId: dimIdForUpdate(form.campaignId, editOriginal?.campaignId),
          contestId: dimIdForUpdate(form.contestId, editOriginal?.contestId),
          agentUserId: dimIdForUpdate(form.agentUserId, editOriginal?.agentUserId),
          projectCode: form.projectCode.trim() || (editOriginal?.projectCode ? '' : undefined),
          ...(finDims.enabled ? {
            paymentMethod: form.paymentMethod || (editOriginal?.paymentMethod ? '' : undefined),
            businessUnitId: dimIdForUpdate(form.businessUnitId, editOriginal?.businessUnitId),
            siteId: dimIdForUpdate(form.siteId, editOriginal?.siteId),
            teamId: dimIdForUpdate(form.teamId, editOriginal?.teamId),
          } : {}),
        }
        await expensesApi.update(editId, body)
      } else {
        const body: ExpenseCreateBody = {
          supplierId: form.supplierId ? Number(form.supplierId) : undefined,
          expenseDate: form.expenseDate,
          description: form.description,
          expenseCategoryId: Number(form.expenseCategoryId),
          costCentre: form.costCentre || undefined,
          costCentreId: form.costCentreId ? Number(form.costCentreId) : undefined,
          netAmount: net,
          vatAmount: vat,
          dueDate: form.dueDate || undefined,
          expectedAccountId: form.expectedAccountId ? Number(form.expectedAccountId) : undefined,
          notes: form.notes || undefined,
          operatorId: dimId(form.operatorId),
          productId: dimId(form.productId),
          campaignId: dimId(form.campaignId),
          contestId: dimId(form.contestId),
          agentUserId: dimId(form.agentUserId),
          projectCode: form.projectCode.trim() || undefined,
          ...(finDims.enabled ? {
            paymentMethod: form.paymentMethod || undefined,
            businessUnitId: dimId(form.businessUnitId),
            siteId: dimId(form.siteId),
            teamId: dimId(form.teamId),
          } : {}),
        }
        const created = await expensesApi.create(body)
        // F-079: segnala (senza bloccare) le spese che sembrano duplicate di quella appena creata.
        setDuplicateNotice(created.possibleDuplicates && created.possibleDuplicates.length > 0 ? created.possibleDuplicates : null)
      }
      closeForm()
      void load()
    } catch (e) {
      // 400/409 dal server (es. categoria disattivata, fornitore inesistente) — messaggio reale,
      // nessuna traduzione: api() lo estrae già da { error } (ValidateReferencesAsync/SetAmounts).
      setSaveError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleTransition(x: Expense, status: string) {
    setTransitioning(x.id)
    setRowError(null)
    try {
      await expensesApi.transition(x.id, status)
      void load()
    } catch (e) {
      setRowError({ id: x.id, message: e instanceof Error ? e.message : 'Errore durante la transizione' })
    } finally {
      setTransitioning(null)
    }
  }

  async function handleDecision(x: Expense, decision: 'Approvata' | 'Rifiutata') {
    setDeciding(x.id)
    setRowError(null)
    try {
      await financeControlsApi.decide(x.id, decision, decision === 'Rifiutata' ? rejectReason : undefined)
      setRejectingId(null)
      setRejectReason('')
      void load()
    } catch (e) {
      setRowError({ id: x.id, message: e instanceof Error ? e.message : 'Errore durante la decisione' })
    } finally {
      setDeciding(null)
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    setDeleteError('')
    setDeleteDeps(null)
    try {
      await expensesApi.delete(deleteTarget.id, deleteReason)
      setDeleteTarget(null)
      setDeleteReason('')
      void load()
    } catch (e) {
      if (isForbidden(e)) {
        // Stesso trattamento del banner "Accesso non autorizzato" già usato per il caricamento
        // della lista — mai il messaggio grezzo "Forbidden" dello statusText di fetch. Con il gate
        // corretto (canTransition, vedi sopra) questo ramo non dovrebbe più essere raggiungibile da
        // un utente reale, ma resta un fallback onesto in caso di sessione scaduta/ruolo cambiato
        // dopo il render.
        setDeleteTarget(null)
        setUnauthorized(true)
      } else {
        // Il 409 ("Only expenses still in 'Prevista' can be deleted...") è il messaggio reale del
        // server — nessuna riformulazione qui. F-104: con HAS_DEPENDENCIES il server elenca anche i collegamenti.
        if (e instanceof ApiError && e.code === 'HAS_DEPENDENCIES') {
          setDeleteDeps((e.body as { dependencies?: Record<string, number> } | undefined)?.dependencies ?? {})
        }
        setDeleteError(e instanceof Error ? e.message : 'Errore durante lo spostamento nel cestino')
      }
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div>
      {unauthorized && (
        <Callout tone="warning" icon="lock">
          Accesso non autorizzato a questa sezione.
        </Callout>
      )}

      {duplicateNotice && (
        <Callout tone="warning" action={<IconButton icon="close" aria-label="Chiudi" size="sm" onClick={() => setDuplicateNotice(null)} />}>
          Spesa registrata, ma assomiglia a {duplicateNotice.length === 1 ? 'un’altra spesa' : 'altre spese'} già presenti
          (n. {duplicateNotice.join(', ')}): stesso fornitore o descrizione, stesso totale e data vicina. Controlla che non sia un doppione.
        </Callout>
      )}

      {!unauthorized && (
        <>
          {/* Filtri + azioni */}
          <FilterBar>
            <Select label="Stato"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
            >
              <option value="">Tutti</option>
              {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </Select>
            <Select label="Categoria"
              value={categoryFilter}
              onChange={e => { setCategoryFilter(e.target.value); setPage(1) }}
            >
              <option value="">Tutte</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Select label="Scadenza"
              value={scadenzaFilter}
              onChange={e => { setScadenzaFilter(e.target.value as ScadenzaFilter); setPage(1) }}
            >
              <option value="all">Tutte</option>
              <option value="overdue">Scadute</option>
              <option value="none">Senza scadenza</option>
            </Select>
            <SortResetLink sort={sort} defaultSort={DEFAULT_SORT} onReset={() => handleSort(DEFAULT_SORT)} />
          </FilterBar>
          {error && <Callout tone="error">{error}</Callout>}

          {loading ? (
            <LoadingState />
          ) : visibleItems.length === 0 ? (
            <EmptyState icon="task_alt" title="Nessuna spesa da mostrare" />
          ) : (
                <DataTable
                  footer={totalPages > 1 ? (
                    <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
                  ) : undefined}
                >
                    <thead>
                      <tr>
                        <SortKeyTh label="Data" ascKey="date_asc" descKey="date_desc" sort={sort} onSort={handleSort} className="ps-3" />
                        <th>Descrizione</th>
                        <th>Categoria</th>
                        <th>Fornitore</th>
                        <th className="text-end">Totale</th>
                        <SortKeyTh label="Scadenza" ascKey="due_asc" descKey="due_desc" sort={sort} onSort={handleSort} />
                        <SortKeyTh label="Stato" ascKey="status" sort={sort} onSort={handleSort} />
                        <th className="text-end pe-3">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleItems.map(x => (
                        <tr key={x.id}>
                          <td className="ps-3 text-nowrap">{formatDate(x.expenseDate)}</td>
                          <td className="md-typescale-emphasized-body-medium">
                            {x.description}
                            {/* F-092: le dimensioni analitiche assegnate, come etichette. */}
                            <div className="d-flex gap-1 flex-wrap">
                              {([
                                ['Operatore', dimensionLabel('operator', x.operatorId)],
                                ['Prodotto', dimensionLabel('product', x.productId)],
                                ['Campagna', dimensionLabel('campaign', x.campaignId)],
                                ['Gara', dimensionLabel('contest', x.contestId)],
                                ['Agente', dimensionLabel('agent', x.agentUserId)],
                                ['Progetto', x.projectCode ?? null],
                                ['Pagamento', PAYMENT_METHODS.find(m => m.id === x.paymentMethod)?.label ?? null],
                                ['BU', finDims.label('bu', x.businessUnitId)],
                                ['Sede', finDims.label('sede', x.siteId)],
                                ['Team', finDims.label('team', x.teamId)],
                              ] as const).filter(([, v]) => v).map(([k, v]) => (
                                <StatusPill key={k} tone="neutral">{k}: {v}</StatusPill>
                              ))}
                            </div>
                          </td>
                          <td className="text-muted">{categoryName(x.expenseCategoryId)}</td>
                          <td className="text-muted">{supplierName(x.supplierId)}</td>
                          <td className="md-num text-end text-nowrap">{formatEur(x.totalAmount)}</td>
                          <td className="text-nowrap">{formatDate(x.dueDate)}</td>
                          <td>
                            <StatusPill tone={pillTone(STATUS_COLORS[x.status])}>
                              {STATUS_LABELS[x.status] ?? x.status}
                            </StatusPill>
                            {approvalInfo[x.id] && (
                              <StatusPill
                                tone="info"
                                className="ms-1"
                                title={approvalInfo[x.id]!.requirement.isDefault
                                  ? 'Nessuna regola di approvazione: basta un’approvazione'
                                  : `Regola: ${approvalInfo[x.id]!.requirement.policyName}`}
                              >
                                Approvazioni {approvalInfo[x.id]!.approvedCount}/{approvalInfo[x.id]!.requirement.requiredApprovals}
                              </StatusPill>
                            )}
                          </td>
                          <td className="text-end pe-3">
                            <div className="d-flex gap-1 justify-content-end flex-wrap">
                              {canWrite && (
                                <IconButton icon="edit" size="sm"
                                  aria-label="Modifica"
                                  onClick={() => openEdit(x)}
                                />
                              )}
                              {/* DELETE /api/expenses/{id} è [Authorize(PolicyNames.Admin)], la
                                  stessa policy della transizione — non EqualStaff. Il gate corretto
                                  è quindi canTransition, non canWrite (bug di review: cinque ruoli
                                  EqualStaff non-Admin vedevano un pulsante che rispondeva 403). */}
                              {canTransition && x.status === 'Prevista' && (
                                <IconButton icon="delete" size="sm"
                                  aria-label="Sposta nel cestino"
                                  onClick={() => { setDeleteError(''); setDeleteDeps(null); setDeleteReason(''); setDeleteTarget(x) }}
                                />
                              )}
                            </div>
                            {/* Transizioni di stato — SOLO quelle che il server dichiara ammesse
                                (x.availableTransitions), mai calcolate qui. Nascoste (non
                                disabilitate) a chi non è Admin: quella richiesta risponderebbe
                                comunque 403 lato server. */}
                            {/* F-079: approvazione a matrice. Chi può decidere adesso (lo dice il server) vede Approva e
                                Rifiuta; il rifiuto vuole un motivo. */}
                            {approvalInfo[x.id]?.canDecide && rejectingId !== x.id && (
                              <div className="d-flex gap-1 justify-content-end flex-wrap mt-1">
                                <Button
                                  type="button" variant="text" size="sm"
                                  disabled={deciding === x.id}
                                  onClick={() => void handleDecision(x, 'Approvata')}
                                >
                                  Approva
                                </Button>
                                <Button
                                  type="button" variant="text" size="sm"
                                  disabled={deciding === x.id}
                                  onClick={() => { setRejectingId(x.id); setRejectReason('') }}
                                >
                                  Rifiuta
                                </Button>
                              </div>
                            )}
                            {rejectingId === x.id && (
                              <div className="d-flex gap-1 justify-content-end flex-wrap mt-1">
                                <TextField
                                  labelHidden
                                  label="Motivo del rifiuto"
                                  placeholder="Motivo del rifiuto"
                                  maxLength={500}
                                  value={rejectReason}
                                  onChange={e => setRejectReason(e.target.value)}
                                />
                                <Button type="button" variant="text" size="sm" onClick={() => setRejectingId(null)}>
                                  Annulla
                                </Button>
                                <Button
                                  type="button" variant="text" tone="error" size="sm"
                                  disabled={deciding === x.id || !rejectReason.trim()}
                                  onClick={() => void handleDecision(x, 'Rifiutata')}
                                >
                                  Conferma rifiuto
                                </Button>
                              </div>
                            )}
                            {canTransition && x.availableTransitions.length > 0 && (
                              <div className="d-flex gap-1 justify-content-end flex-wrap mt-1">
                                {x.availableTransitions
                                  // F-079: con una regola di approvazione, "Approvata" si ottiene dai pulsanti Approva/Rifiuta
                                  // (il server risponderebbe 409 al clic diretto).
                                  .filter(next => !(next === 'Approvata' && x.status === 'Prevista' && approvalInfo[x.id] && !approvalInfo[x.id]!.requirement.isDefault))
                                  .map(next => (
                                  <Button
                                    key={next}
                                    type="button"
                                    variant="text"
                                    size="sm"
                                    disabled={transitioning === x.id}
                                    onClick={() => void handleTransition(x, next)}
                                  >
                                    → {STATUS_LABELS[next] ?? next}
                                  </Button>
                                ))}
                              </div>
                            )}
                            {!canTransition && x.availableTransitions.length > 0 && (
                              <div className="text-muted md-typescale-body-small mt-1">
                                Cambio stato riservato agli amministratori
                              </div>
                            )}
                            {rowError?.id === x.id && (
                              <div className="text-danger md-typescale-body-small mt-1">{rowError.message}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
          )}
        </>
      )}

      {/* Create/Edit */}
      <Dialog
        open={showForm}
        onClose={closeForm}
        title={editId !== null ? 'Modifica spesa' : 'Nuova spesa'}
        as="form"
        onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={closeForm}>Annulla</Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        {saveError && <Callout tone="error">{saveError}</Callout>}
        <FormSection title="Spesa">
          <TextField label="Descrizione *" type="text"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
          <Select label="Categoria *"
            value={form.expenseCategoryId}
            onChange={e => setForm(f => ({ ...f, expenseCategoryId: e.target.value }))}
          >
            <option value="">Seleziona...</option>
            {categories.filter(c => c.isActive).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
          <TextField label="Data" type="date"
            value={form.expenseDate}
            onChange={e => setForm(f => ({ ...f, expenseDate: e.target.value }))}
          />
          <TextField label="Scadenza" type="date"
            value={form.dueDate}
            onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
          />
        </FormSection>
        <FormSection title="Collegamenti">
          <TextField label="Centro di costo" type="text"
            value={form.costCentre}
            onChange={e => setForm(f => ({ ...f, costCentre: e.target.value }))}
          />
          <Select label="Centro di costo (anagrafica)" supportingText={costCentres.length === 0 ? '(nessuno disponibile)' : undefined}
            value={form.costCentreId}
            onChange={e => setForm(f => ({ ...f, costCentreId: e.target.value }))}
          >
            <option value="">—</option>
            {costCentres.map(c => (
              <option key={c.id} value={c.id}>{c.code} · {c.name}</option>
            ))}
          </Select>
          <Select label="Fornitore" supportingText={suppliers.length === 0 ? '(nessuno disponibile)' : undefined}
            value={form.supplierId}
            onChange={e => setForm(f => ({ ...f, supplierId: e.target.value }))}
          >
            <option value="">Nessuno</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.companyName}</option>)}
          </Select>
          <Select label="Conto previsto" supportingText={accounts.length === 0 ? '(nessuno disponibile)' : undefined}
            value={form.expectedAccountId}
            onChange={e => setForm(f => ({ ...f, expectedAccountId: e.target.value }))}
          >
            <option value="">Nessuno</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </FormSection>
        <FormSection
          title="Importi"
          description="Solo un'anteprima: il totale reale è calcolato e verificato dal server."
        >
          <TextField label="Importo netto *" type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={form.netAmount}
            onChange={e => setForm(f => ({ ...f, netAmount: e.target.value }))}
          />
          <TextField label="IVA *" type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={form.vatAmount}
            onChange={e => setForm(f => ({ ...f, vatAmount: e.target.value }))}
          />
          <TextField label="Totale (anteprima)" type="text"
            disabled
            value={totalPreview !== null ? formatEur(totalPreview) : '—'}
          />
        </FormSection>
        <FormSection
          title="Dimensioni"
          description={<>Facoltative: servono a leggere i costi per operatore, prodotto, campagna, gara, agente o progetto
            (Controllo costi, «Per dimensione»). Non cambiano gli importi.</>}
        >
          <DimensionPicker label="Operatore" options={dimensionOptions.operator} value={form.operatorId}
            onChange={v => setForm(f => ({ ...f, operatorId: v }))} />
          <DimensionPicker label="Prodotto" options={dimensionOptions.product} value={form.productId}
            onChange={v => setForm(f => ({ ...f, productId: v }))} />
          <DimensionPicker label="Campagna" options={dimensionOptions.campaign} value={form.campaignId}
            onChange={v => setForm(f => ({ ...f, campaignId: v }))} />
          <DimensionPicker label="Gara" options={dimensionOptions.contest} value={form.contestId}
            onChange={v => setForm(f => ({ ...f, contestId: v }))} />
          <DimensionPicker label="Agente" options={dimensionOptions.agent} value={form.agentUserId}
            onChange={v => setForm(f => ({ ...f, agentUserId: v }))} />
          <TextField label="Progetto" type="text"
            maxLength={50}
            placeholder="Codice progetto (facoltativo)"
            value={form.projectCode}
            onChange={e => setForm(f => ({ ...f, projectCode: e.target.value }))}
          />
        </FormSection>
        {finDims.enabled && (
          <FormSection
            title="Pagamento e struttura"
            description="Facoltativi: metodo di pagamento e, se configurate, business unit, sede e team."
          >
            <Select label="Metodo di pagamento" value={form.paymentMethod}
              onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}>
              <option value="">Non indicato</option>
              {PAYMENT_METHODS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </Select>
            {finDims.businessUnits.length > 0 && (
              <DimensionPicker label="Business unit" options={finDims.businessUnits} value={form.businessUnitId}
                onChange={v => setForm(f => ({ ...f, businessUnitId: v }))} />
            )}
            {finDims.sites.length > 0 && (
              <DimensionPicker label="Sede" options={finDims.sites} value={form.siteId}
                onChange={v => setForm(f => ({ ...f, siteId: v }))} />
            )}
            <DimensionPicker label="Team" options={finDims.teams} value={form.teamId}
              onChange={v => setForm(f => ({ ...f, teamId: v }))} />
          </FormSection>
        )}
        <FormGrid columns={1}>
          <TextField multiline label="Note"
            rows={3}
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          />
        </FormGrid>
              </Dialog>

      {/* Conferma spostamento nel cestino (F-104: non si cancella piu' nulla) */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        dismissible={!deleting}
        title="Spostare la spesa nel cestino?"
        supportingText={deleteTarget && (
          <>
            <strong>{deleteTarget.description}</strong> esce da elenchi, scadenziario e controllo costi, ma resta in
            archivio e si ripristina da Impostazioni &gt; Cestino. Possibile solo perché è ancora in stato <em>Prevista</em> e
            non ha approvazioni, movimenti dei soci o quote bancarie collegate.
          </>
        )}
        size="sm"
        as="form"
        onSubmit={(e) => { e.preventDefault(); void confirmDelete() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Annulla
            </Button>
            <Button type="submit" variant="text" tone="error" disabled={deleting}>
              {deleting ? 'Spostamento...' : 'Sposta nel cestino'}
            </Button>
          </>
        }
      >
        {deleteTarget && (
          <>
            {deleteError && <Callout tone="warning">{deleteError}</Callout>}
            {deleteDeps && Object.keys(deleteDeps).length > 0 && (
              <ul className="md-typescale-body-small text-danger ps-3" data-testid="expense-trash-dependencies">
                {Object.entries(deleteDeps).map(([area, n]) => (
                  <li key={area}>{area}: {n}</li>
                ))}
              </ul>
            )}
            <FormGrid columns={1}>
              <TextField
                label="Motivo (facoltativo)"
                id="expense-trash-reason"
                type="text"
                maxLength={500}
                value={deleteReason}
                onChange={e => setDeleteReason(e.target.value)}
                supportingText="Es. inserita due volte"
                disabled={deleting}
              />
            </FormGrid>
          </>
        )}
      </Dialog>
    </div>
  )
}

/* ── Categorie ───────────────────────────────────────────────────────────────────────────────── */

function CategoriesTab({ canWrite }: { canWrite: boolean }) {
  const [items, setItems] = useState<ExpenseCategory[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  const [togglingId, setTogglingId] = useState<number | null>(null)
  const [toggleError, setToggleError] = useState<{ id: number; message: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setItems(await expenseCategoriesApi.list(true))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function handleCreate() {
    if (!name.trim()) { setCreateError('Il nome è obbligatorio'); return }
    setCreating(true)
    setCreateError('')
    try {
      await expenseCategoriesApi.create(name.trim())
      setName('')
      void load()
    } catch (e) {
      // 400 con { error } per nome duplicato — messaggio reale del server, verbatim.
      setCreateError(e instanceof Error ? e.message : 'Errore durante la creazione')
    } finally {
      setCreating(false)
    }
  }

  async function handleToggle(c: ExpenseCategory) {
    setTogglingId(c.id)
    setToggleError(null)
    try {
      await expenseCategoriesApi.update(c.id, { isActive: !c.isActive })
      void load()
    } catch (e) {
      setToggleError({ id: c.id, message: e instanceof Error ? e.message : 'Errore durante l\'aggiornamento' })
    } finally {
      setTogglingId(null)
    }
  }

  return (
    <div>
      {canWrite && (
        <Card className="mb-3">
          <CardContent>
            <div className="d-flex gap-2 align-items-end flex-wrap">
              <div className="flex-grow-1 md-u-minw-220">
                <TextField label="Nuova categoria" type="text"
                  placeholder="Nome categoria"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>
              <Button type="button" variant="filled" disabled={creating} onClick={() => void handleCreate()}>
                {creating ? 'Creazione...' : 'Aggiungi'}
              </Button>
            </div>
            {createError && <div className="text-danger md-typescale-body-small mt-2">{createError}</div>}
          </CardContent>
        </Card>
      )}

      {error && <Callout tone="error">{error}</Callout>}

      {loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState icon="category" title="Nessuna categoria configurata" />
      ) : (
            <DataTable>
                <thead>
                  <tr>
                    <th className="ps-3">Nome</th>
                    <th>Stato</th>
                    {canWrite && <th className="text-end pe-3">Azioni</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map(c => (
                    <tr key={c.id}>
                      <td className="ps-3 md-typescale-emphasized-body-medium">{c.name}</td>
                      <td>
                        {c.isActive
                          ? <StatusPill tone="success">Attiva</StatusPill>
                          : <StatusPill tone="neutral">Disattivata</StatusPill>}
                      </td>
                      {canWrite && (
                        <td className="text-end pe-3">
                          <Button
                            variant="text"
                            size="sm"
                            disabled={togglingId === c.id}
                            onClick={() => void handleToggle(c)}
                          >
                            {c.isActive ? 'Disattiva' : 'Attiva'}
                          </Button>
                          {toggleError?.id === c.id && (
                            <div className="text-danger md-typescale-body-small mt-1">{toggleError.message}</div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </DataTable>
      )}
    </div>
  )
}
